"use client"
import styled from "@emotion/styled"
import { FormControlLabel, Radio, RadioGroup } from "@mui/material"
import { Controller } from "react-hook-form"
import { FormHelperErrorIcon, NewText, RadioCheckedIcon, RadioUnCheckedIcon } from "ui"

const StyledContainer = styled.div`
  margin: 16px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 24px 0;
  }
`

const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
`

type Props = {
  title: string
  desktopFontSize?: number
  mobileFontSize?: number
  control: any
  name: string
  style?: any
  yes: string
  no: string
  error?: boolean
  errorMessage?: string
  externalHeadingId?: string
}

export const RadioButtonController: React.FC<Props> = ({
  desktopFontSize = 24,
  mobileFontSize = 18,
  title,
  control,
  name,
  style,
  no,
  yes,
  error = false,
  errorMessage,
  externalHeadingId,
}) => {
  const errorId = `${name}-error`
  const headingId = externalHeadingId || `${name}-heading`

  return (
    <StyledContainer style={style}>
      <NewText
        id={headingId}
        desktopFontSize={desktopFontSize}
        desktopFontWeight="400"
        desktopLineHeight="100%"
        mobileFontSize={mobileFontSize}
        mobileFontWeight="400"
        mobileLineHeight={"100%"}
        component="p"
        color={"black"}
        marginBottom="16px"
      >
        {title}
      </NewText>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <RadioGroup
              row
              {...field}
              aria-labelledby={headingId}
              aria-required="true"
              aria-describedby={error && errorMessage ? errorId : undefined}
            >
              <FormControlLabel
                sx={{
                  fontSize: "18px",
                  "& .MuiRadio-root:focus-visible": {
                    outline: "2px solid currentColor",
                    outlineOffset: "2px",
                    borderRadius: "50%",
                  },
                }}
                value={true}
                control={
                  <Radio
                    icon={<RadioUnCheckedIcon error={error} />}
                    checkedIcon={<RadioCheckedIcon />}
                    inputProps={{ "aria-invalid": error || undefined } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={
                  <NewText
                    desktopFontSize={18}
                    desktopFontWeight="400"
                    desktopLineHeight="100%"
                    mobileFontSize={16}
                    mobileFontWeight="400"
                    mobileLineHeight={"100%"}
                    component="p"
                    color={"black"}
                  >
                    {yes}
                  </NewText>
                }
              />
              <FormControlLabel
                sx={{
                  fontSize: "18px",
                  "& .MuiRadio-root:focus-visible": {
                    outline: "2px solid currentColor",
                    outlineOffset: "2px",
                    borderRadius: "50%",
                  },
                }}
                value={false}
                control={
                  <Radio
                    icon={<RadioUnCheckedIcon error={error} />}
                    checkedIcon={<RadioCheckedIcon />}
                    inputProps={{ "aria-invalid": error || undefined } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={
                  <NewText
                    desktopFontSize={18}
                    desktopFontWeight="400"
                    desktopLineHeight="100%"
                    mobileFontSize={16}
                    mobileFontWeight="400"
                    mobileLineHeight={"100%"}
                    component="p"
                    color={"black"}
                  >
                    {no}
                  </NewText>
                }
              />
            </RadioGroup>
          )
        }}
      />
      {error && errorMessage && (
        <StyledErrorContainer id={errorId} role="alert">
          <FormHelperErrorIcon />
          <p
            style={{
              margin: 0,
              textAlign: "right",
              color: "#C92418",
              marginRight: "4px",
              fontSize: "12px",
            }}
          >
            {errorMessage}
          </p>
        </StyledErrorContainer>
      )}
    </StyledContainer>
  )
}
export default RadioButtonController
